import { FileDiff } from 'lucide-react'
import { useRef } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { evidenceText } from '@/lib/siteHealth'
import { useAnalytics } from '@/provider/useAuth'
import { EYEBROW_CLASS } from './SiteHealthStyles'
import type { TextDiffDialogProps } from './siteHealthComponents.types'

export default function TextDiffDialog({
  evidence,
  findingId,
  pageId,
}: TextDiffDialogProps) {
  const analytics = useAnalytics()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const rawText = evidenceText(evidence, 'raw')
  const renderedText = evidenceText(evidence, 'rendered')

  if (rawText === null && renderedText === null) return null

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open) {
          window.setTimeout(() => triggerRef.current?.focus(), 0)
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="link"
          ref={triggerRef}
          onClick={() => {
            const subject = findingId ? { findingId } : { pageId }

            analytics.track('site_health_finding_expanded', {
              ...subject,
              evidence: 'text-diff',
            })
          }}
          className="h-auto p-0"
        >
          <FileDiff className="size-4" aria-hidden="true" />
          View text diff
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] gap-6 overflow-y-auto p-8 sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>Raw and rendered text evidence</DialogTitle>
          <DialogDescription>
            This comparison shows the bounded text evidence returned for this
            page or finding.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <section className="min-w-0 rounded-xl border border-border bg-muted/30 p-4">
            <h3 className={EYEBROW_CLASS}>Raw fetch</h3>
            <pre
              aria-label="Raw fetch evidence"
              className="mt-3 max-h-[55vh] overflow-auto whitespace-pre-wrap break-words text-sm leading-6"
            >
              {rawText ?? 'No raw evidence returned.'}
            </pre>
          </section>
          <section className="min-w-0 rounded-xl border border-border bg-muted/30 p-4">
            <h3 className={EYEBROW_CLASS}>Rendered fetch</h3>
            <pre
              aria-label="Rendered fetch evidence"
              className="mt-3 max-h-[55vh] overflow-auto whitespace-pre-wrap break-words text-sm leading-6"
            >
              {renderedText ??
                'Not evaluated: rendered evidence was not returned.'}
            </pre>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  )
}
